import { useMemo } from "react";
import { TagButton } from "@root/shared/components/TagButton";
import { type ValueOf } from "@root/shared/types/type";
import { type ChoiceGroup, type Choice } from "../../../types";
import * as styles from "./styles.css";

const CATEGORY_TYPE = {
  POPULAR_AREA: "popularArea",
  CITY: "city",
  LINE: "line",
  STATION: "station",
} as const;

export type CategoryType = ValueOf<typeof CATEGORY_TYPE>;

export type CategorizedChoice = {
  label: string;
  value: string;
  category: CategoryType;
};

export type SelectedChoicesProps = {
  popularAreaValues: string[];
  onPopularAreaRemove: (newValues: string[]) => void;
  popularAreaChoices: Choice[];
  cityValues: string[];
  onCityRemove: (newValues: string[]) => void;
  cityChoices: ChoiceGroup[];
  lineValues: string[];
  onLineRemove: (newValues: string[]) => void;
  lineChoices: ChoiceGroup[];
};

export const SelectedChoices = (props: SelectedChoicesProps) => {
  const {
    popularAreaValues,
    onPopularAreaRemove,
    popularAreaChoices,
    cityValues,
    onCityRemove,
    cityChoices,
    lineValues,
    onLineRemove,
    lineChoices,
  } = props;

  const selectedChoices = useMemo(() => {
    const allChoices: CategorizedChoice[] = [
      ...popularAreaChoices.map(({ label, value }) => ({
        label,
        value,
        category: CATEGORY_TYPE.POPULAR_AREA,
      })),
      ...cityChoices.map((choice) => ({
        label: choice.groupName,
        value: choice.value,
        category: CATEGORY_TYPE.CITY,
      })),
      ...cityChoices.flatMap(({ choices }) =>
        choices.map(({ label, value }) => ({
          label,
          value,
          category: CATEGORY_TYPE.CITY,
        })),
      ),
      ...lineChoices.map((choice) => ({
        label: choice.groupName,
        value: choice.value,
        category: CATEGORY_TYPE.LINE,
      })),
      ...lineChoices.flatMap(({ choices }) =>
        choices.map(({ label, value }) => ({
          label,
          value,
          category: CATEGORY_TYPE.STATION,
        })),
      ),
    ];

    const allValuesSet = new Set([...popularAreaValues, ...cityValues, ...lineValues]);
    const seenValues = new Set<string>();

    return allChoices.filter(({ value }) => {
      if (!allValuesSet.has(value) || seenValues.has(value)) return false;
      seenValues.add(value);
      return true;
    });
  }, [popularAreaChoices, cityChoices, lineChoices, popularAreaValues, cityValues, lineValues]);

  const handleRemove = (option: CategorizedChoice) => {
    if (option.category === CATEGORY_TYPE.POPULAR_AREA) {
      const newValues = popularAreaValues.filter((v) => v !== option.value);
      onPopularAreaRemove(newValues);
    } else if (option.category === CATEGORY_TYPE.CITY) {
      const newValues = cityValues.filter((v) => v !== option.value);
      onCityRemove(newValues);
    } else if (
      option.category === CATEGORY_TYPE.LINE ||
      option.category === CATEGORY_TYPE.STATION
    ) {
      const newValues = lineValues.filter((v) => v !== option.value);
      onLineRemove(newValues);
    }
  };

  return (
    <>
      {selectedChoices.length > 0 && (
        <div className={styles.wrapper}>
          {selectedChoices.map((option) => (
            <TagButton key={option.label} theme="removable" onClick={() => handleRemove(option)}>
              {`${CATEGORY_PREFIXES[option.category]}${option.label}`}
            </TagButton>
          ))}
        </div>
      )}
    </>
  );
};

const CATEGORY_PREFIXES = {
  popularArea: "",
  city: "",
  line: "路線：",
  station: "駅：",
} satisfies Record<CategoryType, string>;
